import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { NotificationContext } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';

const LABEL = { like: 'liked your highlight', comment: 'commented on your highlight' };

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const NotificationBell = () => {
  const { isLoggedIn }                     = useContext(AuthContext);
  const { notifications, unreadCount, markRead, markAllRead } = useContext(NotificationContext);
  const [open, setOpen] = useState(false);
  const wrapperRef      = useRef(null);
  const navigate        = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isLoggedIn) return null;

  const handleNotificationClick = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    navigate('/highlights');
  };

  const handleMarkAll = (e) => {
    e.stopPropagation();
    markAllRead();
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '2px' }}
      >
        <Bell size={18} strokeWidth={1.8} color={open ? '#000' : '#555'} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -4,
            background: '#e03030', color: '#fff',
            fontSize: '.55rem', fontWeight: 900,
            minWidth: 14, height: 14, borderRadius: 7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', lineHeight: 1, pointerEvents: 'none',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0,
          width: 320, background: '#fff', border: '1px solid #e5e5e5',
          boxShadow: '0 4px 20px rgba(0,0,0,.08)', zIndex: 200,
          maxHeight: 420, overflowY: 'auto',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '.85rem', letterSpacing: '.05em' }}>NOTIFICATIONS</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAll} style={{ background: 'none', border: 'none', fontSize: '.75rem', color: '#888', cursor: 'pointer', padding: 0 }}>
                mark all read
              </button>
            )}
          </div>

          {/* List */}
          {notifications.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa', fontSize: '.8rem', fontFamily: '"JetBrains Mono", monospace', letterSpacing: '.05em' }}>
              NO NOTIFICATIONS YET
            </div>
          ) : (
            notifications.slice(0, 15).map(n => (
              <button
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  width: '100%', padding: '10px 14px', border: 'none',
                  borderBottom: '1px solid #f8f8f8', cursor: 'pointer', textAlign: 'left',
                  background: n.read ? '#fff' : '#fffdf0',
                  transition: 'background .1s',
                }}
              >
                {n.actor_avatar
                  ? <img src={n.actor_avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: 1 }} />
                  : <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#e5e5e5', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 700, marginTop: 1 }}>
                      {(n.actor_username || '?')[0].toUpperCase()}
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '.82rem', lineHeight: 1.4 }}>
                    <strong>{n.actor_username}</strong>{' '}
                    <span style={{ color: '#555' }}>{LABEL[n.type] || n.type}</span>
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '.72rem', color: '#aaa' }}>{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#e03030', flexShrink: 0, marginTop: 5 }} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
